import i18n from '../../i18n';

/** Normalize backend codes, e.g. "INVALID VALUE" → "INVALID_VALUE". */
export function normalizeApiErrorCode(code) {
  if (!code || typeof code !== 'string') return '';
  return code.trim().replace(/\s+/g, '_').toUpperCase();
}

/** Pick the best machine-readable code from an apiFetch error. */
export function getApiErrorCode(err) {
  if (!err) return '';

  if (typeof err === 'string') {
    const trimmed = err.trim();
    if (/^[A-Z0-9_ ]+$/i.test(trimmed)) {
      return normalizeApiErrorCode(trimmed);
    }
  }

  // Handle 502, 503, 504 server/proxy errors
  if (
    err.status === 502 ||
    err.status === 503 ||
    err.status === 504 ||
    (typeof err.message === 'string' &&
      (err.message.includes('502') || err.message.includes('503') || err.message.includes('504')))
  ) {
    return 'SERVER_ERROR';
  }

  // Handle connection/network failures
  if (
    err.name === 'TypeError' &&
    typeof err.message === 'string' &&
    (err.message === 'Failed to fetch' ||
      err.message.includes('NetworkError') ||
      err.message.includes('network'))
  ) {
    return 'CONNECTION_ERROR';
  }

  let fromData =
    err?.data?.error ||
    err?.data?.code ||
    err?.response?.data?.error ||
    err?.response?.data?.code;

  if (!fromData && (err?.data?.errors || err?.response?.data?.errors)) {
    const errorsObj = err?.data?.errors || err?.response?.data?.errors;
    if (typeof errorsObj === 'object' && errorsObj !== null) {
      const firstVal = Object.values(errorsObj)[0];
      if (typeof firstVal === 'string') {
        fromData = firstVal;
      }
    }
  }

  if (fromData && /^[A-Z0-9_ ]+$/i.test(String(fromData).trim())) {
    return normalizeApiErrorCode(String(fromData));
  }

  const message = err?.message;
  if (message && /^[A-Z0-9_ ]+$/i.test(message.trim())) {
    return normalizeApiErrorCode(message);
  }

  const detail = err?.response?.data?.detail || err?.data?.detail;
  if (detail && typeof detail === 'string' && /^[A-Z0-9_ ]+$/i.test(detail.trim())) {
    return normalizeApiErrorCode(detail);
  }

  // Handle common raw backend error messages that have explicit codes
  const rawMsg = typeof err === 'string' ? err : (message || detail);
  if (typeof rawMsg === 'string') {
    if (/curriculum mapping.*already exists/i.test(rawMsg)) {
      return 'CURRICULUM_MAPPING_EXISTS';
    }
    if (/course_curriculum_mappings.*missing/i.test(rawMsg)) {
      return 'CURRICULUM_MAPPING_NOT_FOUND';
    }
  }

  return '';
}

/** Translate via guest_api_errors or api_errors; falls back to human detail/message. */
export function translateApiError(tArg, errArg, fallbackKeyArg) {
  let t = tArg;
  let err = errArg;
  let fallbackKey = fallbackKeyArg;

  // Support flexible signature: translateApiError(err, fallbackKey)
  if (typeof tArg !== 'function') {
    fallbackKey = errArg;
    err = tArg;
    t = null;
  }

  const code = getApiErrorCode(err);

  if (code) {
    // 1. Try global i18n instance first to ensure translation regardless of component hook namespaces
    if (i18n.exists(`guest_api_errors:${code}`)) {
      return i18n.t(`guest_api_errors:${code}`);
    }
    if (i18n.exists(`api_errors:${code}`)) {
      return i18n.t(`api_errors:${code}`);
    }
    if (i18n.exists(`school_dashboard:api_errors.${code}`)) {
      return i18n.t(`school_dashboard:api_errors.${code}`);
    }
    if (t && typeof t === 'function') {
      const res = t(`guest_api_errors:${code}`, { defaultValue: '' });
      if (res && res !== `guest_api_errors:${code}`) return res;
      const schoolRes = t(`school_dashboard:api_errors.${code}`, { defaultValue: '' });
      if (schoolRes && schoolRes !== `school_dashboard:api_errors.${code}`) return schoolRes;
    }
  }

  const detailMsg = err?.response?.data?.detail || err?.data?.detail || (typeof err === 'string' ? err : err?.message);

  // 2. If detailMsg is an error code string, try translating it
  if (detailMsg && typeof detailMsg === 'string') {
    const normalizedDetail = normalizeApiErrorCode(detailMsg);
    if (normalizedDetail && i18n.exists(`guest_api_errors:${normalizedDetail}`)) {
      return i18n.t(`guest_api_errors:${normalizedDetail}`);
    }
    if (normalizedDetail && i18n.exists(`api_errors:${normalizedDetail}`)) {
      return i18n.t(`api_errors:${normalizedDetail}`);
    }
    if (normalizedDetail && i18n.exists(`school_dashboard:api_errors.${normalizedDetail}`)) {
      return i18n.t(`school_dashboard:api_errors.${normalizedDetail}`);
    }
  }

  // 3. Fallback to fallbackKey translation if provided
  if (fallbackKey) {
    if (t && typeof t === 'function') {
      const res = t(fallbackKey, { defaultValue: '' });
      if (res && res !== fallbackKey) return res;
    }
    if (i18n.exists(fallbackKey)) {
      return i18n.t(fallbackKey);
    }
  }

  // 4. Return detailMsg if it's human-readable text
  if (detailMsg && typeof detailMsg === 'string' && !/^[A-Z0-9_]+$/.test(detailMsg.trim())) {
    return detailMsg;
  }

  return err?.message || code || 'حدث خطأ غير متوقع';
}

